import { BareButton } from "@setel/web-ui";
import { SkeletonItem } from "~/lib/components/ui/SkeletonItem";
import { LocaleLink } from "~/lib/components/link";
import React, { useState } from "react";
import { useAuth } from "~/lib/utils/hooks/useAuth";
import { useTransactions } from "~/lib/utils/hooks/useTransactions";
import { useTranslation } from "~/lib/utils/store/useTranslation";
import { ITransactionsApiTypes } from "~/lib/types/variables.type";
import { TransactionCardOverview } from "../transaction/TransactionCard";
import { trackUserEvent } from "../../../utils/analytics";
import { EVENT_CHANNELS, EVENT_TYPES } from "../../../utils/analytics.events";
import { useHelpCentreConfig } from "~/services/queries/variable.queries";
import cn from "classnames";

const RecentActivities = ({
  isVisibleTab = true,
  pricePosition,
}: {
  isVisibleTab?: boolean;
  pricePosition?: "right" | "bottom";
}) => {
  const { isAuthorized } = useAuth();
  const [asPathToPage, setAsPathToPage] = useState<string>("/topics/id/name");
  const { data: helpCentreConfig, isLoading: isLoadingHelpConfig } =
    useHelpCentreConfig();
  const { t } = useTranslation();
  const TABS = [
    {
      label: t("payment"),
      value: ITransactionsApiTypes.PAYMENT,
    },
    {
      label: t("mesra"),
      value: ITransactionsApiTypes.LOYALTY,
    },
  ];
  const [selectedTab, setSelectedTab] = useState(TABS[0].value);
  const { data: payments, isLoading: isLoadingPayment } = useTransactions({
    enabled: isAuthorized,
    perPage: 1,
    transactionType: ITransactionsApiTypes.PAYMENT,
  });

  const { data: loyalties, isLoading: isLoadingLoyalty } = useTransactions({
    enabled: isAuthorized && isVisibleTab, // if no tab, we no need to fetch loyalty data
    perPage: 1,
    transactionType: ITransactionsApiTypes.LOYALTY,
  });

  const renderTabItem = () => {
    if (selectedTab === ITransactionsApiTypes.PAYMENT) {
      if (isLoadingPayment) {
        return <SkeletonItem count={1} />;
      }

      if (payments?.length) {
        const transaction = payments[0];
        return (
          <TransactionCardOverview
            transaction={transaction}
            link={`/transaction/payment/${transaction.id}`}
            pricePosition={pricePosition}
          />
        );
      }
    }

    if (selectedTab === ITransactionsApiTypes.LOYALTY) {
      if (isLoadingLoyalty) {
        return <SkeletonItem count={1} />;
      }

      if (loyalties?.length) {
        const transaction = loyalties[0];

        return (
          <TransactionCardOverview
            transaction={transaction}
            link={`/transaction/mesra/${transaction.id}`}
            pricePosition={pricePosition}
          />
        );
      }
    }

    // Nothing found
    return (
      <div className="font-normal text-[15px] leading-[21px] text-gray-dark">
        You have no transaction
      </div>
    );
  };

  return (
    <>
      <div className="flex justify-between items-center">
        <p className="font-semibold text-xl font-title">
          {t("latestTransaction")}
        </p>
        <LocaleLink to="/transaction">
          <BareButton
            className="font-medium text-base font-secondary"
            onClick={(e) => {
              trackUserEvent(
                EVENT_TYPES.WEB_SUPPORT_HELP_CENTRE_RECENT_ACTIVITIES_VIEW_ALL,
                {},
                [EVENT_CHANNELS.MIXPANEL]
              );
            }}
          >
            {t("viewAll")}
          </BareButton>
        </LocaleLink>
      </div>

      <div className="flex flex-col gap-5 md:gap-7">
        {isVisibleTab && (
          <div className="flex flex-row gap-4 border-b border-white-off cursor-pointer">
            {TABS.map((tab) => (
              <div
                key={tab.value}
                className={cn(
                  "px-2 pb-3 font-secondary text-gray-light text-sm font-medium",
                  tab.value === selectedTab
                    ? "text-brand border-b-2 border-brand"
                    : ""
                )}
                onClick={() => {
                  setSelectedTab(tab.value);
                }}
              >
                {tab.label}
              </div>
            ))}
          </div>
        )}
        <div>{renderTabItem()}</div>
      </div>
    </>
  );
};

export default RecentActivities;
